<template>
	<span>{{ display }}</span>
</template>

<script>
/**
 * AnimatedNumber — tweens its displayed value toward `value` whenever the prop
 * changes, giving the dashboard fluid "counting" motion between the 5s stat
 * snapshots instead of hard jumps.
 *
 * CPU-friendly: a requestAnimationFrame loop runs ONLY during the ~600ms
 * transition and cancels itself on arrival, on new values, and on destroy.
 *
 * Props:
 *  - value      raw numeric value (e.g. 0-1 fraction for OEE, or a count)
 *  - multiplier scale applied before formatting (e.g. 100 for fractions → %)
 *  - decimals   fixed decimals to render (0 for integer counts)
 *  - locale     when true + decimals=0, render with thousands separators
 *  - duration   tween duration in ms (default 600)
 */
export default {
	name : 'AnimatedNumber',

	props : {
		value      : { type : Number,  default : 0 },
		multiplier : { type : Number,  default : 1 },
		decimals   : { type : Number,  default : 0 },
		locale     : { type : Boolean, default : false },
		duration   : { type : Number,  default : 600 },
	},

	data() {
		return {
			current : Number(this.value) || 0,
			_raf    : null,
		};
	},

	computed : {
		display() {
			const scaled = this.current * this.multiplier;
			if (this.decimals > 0) {
				return scaled.toFixed(this.decimals);
			}
			const rounded = Math.round(scaled);
			return this.locale ? rounded.toLocaleString() : String(rounded);
		},
	},

	watch : {
		value(newVal) {
			this._animateTo(Number(newVal) || 0);
		},
	},

	beforeDestroy() {
		this._cancel();
	},

	methods : {
		_cancel() {
			if (this._raf) {
				cancelAnimationFrame(this._raf);
				this._raf = null;
			}
		},

		_animateTo(target) {
			this._cancel();

			const from     = this.current;
			const delta    = target - from;
			// Tiny or no change → snap (avoids pointless rAF churn)
			if (Math.abs(delta) < 1e-9) {
				this.current = target;
				return;
			}

			const start    = (typeof performance !== 'undefined' && performance.now)
				? performance.now()
				: Date.now();
			const duration = this.duration > 0 ? this.duration : 1;

			const step = (now) => {
				const elapsed = ((typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now()) - start;
				const t       = Math.min(1, elapsed / duration);
				// easeOutCubic
				const eased   = 1 - Math.pow(1 - t, 3);
				this.current  = from + delta * eased;
				if (t < 1) {
					this._raf = requestAnimationFrame(step);
				} else {
					this.current = target;
					this._raf    = null;
				}
			};

			this._raf = requestAnimationFrame(step);
		},
	},
};
</script>
